import { ChangeEvent, FC, useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import Modal from '../../UIelements/Modal';
import PrimaryButton from '../../FormElements/PrimaryButton/index';
import { db } from '../../../db/db'
import * as Styled from './styles';
import { useLiveQuery } from 'dexie-react-hooks';
import { toast } from 'react-toastify';
import { loadCompanyById, loadEquipmentByCompanyId, updateEquipment } from '../../../services';
import { Equipment, LoadEquipmentByCompanyData, UpdateEquipmentRequestType } from '../../../models/equipment';
import InputCheckBox from '../../FormElements/InputCheckBox';
import { getCurrentDateTimezone } from '../../../utils/formatDateHour';
import { createScrollHandler } from '../../../utils/createScrollHandler';

type ModalProps = {
  onCancel: () => void;
  companyIdRemoteAccess: string;
  equipmentId: number;
  onUpdateList: () => void;
}

const EQUIPMENT_PAGE_SIZE = 9;
const SELECT_ALL_EQUIPMENT_PAGE_SIZE = 100;

const mergeById = <T extends { id: number }>(items: T[]) => {
  return items.reduce<T[]>((acc, current) => {
    if (!acc.some(item => item.id === current.id)) {
      acc.push(current);
    }

    return acc;
  }, []);
};

const ModalRemoteAccess: FC<ModalProps> = (props) => {
  const intl = useIntl();
  const userDb = useLiveQuery(() => db.user.toArray());

  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const contentBodyRef = useRef<HTMLDivElement | null>(null);
  const equipmentRawRef = useRef<LoadEquipmentByCompanyData[]>([]);
  const isFetchingEquipmentsRef = useRef<boolean>(false);
  const hasMoreRef = useRef<boolean>(true);
  const lastSeenEquipmentIdRef = useRef<number | undefined>(undefined);
  const [paginationTick, setPaginationTick] = useState<number>(0);

  const [modal, setModal] = useState<string>("modal1");
  const [companyList, setCompanyList] = useState<any[]>([]);
  const [equipmentList, setEquipmentList] = useState<LoadEquipmentByCompanyData[]>([]);
  const [checkAll, setCheckAll] = useState<boolean>(false);
  const [selectedEquips, setSelectedEquips] = useState<any[]>([]);

  const updateHasMore = useCallback((value: boolean) => {
    hasMoreRef.current = value;
    setHasMore(value);
  }, []);

  const syncEquipmentState = useCallback((equipments: LoadEquipmentByCompanyData[]) => {
    equipmentRawRef.current = equipments;
    setEquipmentList(equipments.filter((equipment) => equipment.id !== props.equipmentId));
    setPaginationTick(prev => prev + 1);
  }, [props.equipmentId]);

  const loadAllEquipments = useCallback(async () => {
    if (!userDb?.length) return [];

    let allEquipments: LoadEquipmentByCompanyData[] = [];
    let lastSeenId: number | undefined;

    while (true) {
      const response = await loadEquipmentByCompanyId({
        user: userDb[0],
        accessToken: userDb[0].accessToken,
        lastSeenId,
        limitResult: SELECT_ALL_EQUIPMENT_PAGE_SIZE
      });

      const newEquipmentList = Array.isArray(response.data) ? response.data : [];

      if (newEquipmentList.length === 0) {
        break;
      }

      allEquipments = mergeById([...allEquipments, ...newEquipmentList]);

      if (newEquipmentList.length < SELECT_ALL_EQUIPMENT_PAGE_SIZE) {
        break;
      }

      lastSeenId = newEquipmentList[newEquipmentList.length - 1]?.id;

      if (!lastSeenId) {
        break;
      }
    }

    return allEquipments;
  }, [userDb]);

  const getSubmitEquipmentData = (
    equipment: Equipment,
  ): UpdateEquipmentRequestType => ({
    equipment: {
      iokPin: equipment.iokPin,
      lastUpdate: getCurrentDateTimezone(),
      idUser: equipment.idUser,
      companyIdRemoteAccess: props.companyIdRemoteAccess
    },
  });

  const submitHandler = () => {
    var success = true;
    if (selectedEquips.length === 0) return toast.error(
      intl.formatMessage({
        id: 'equipment.Selecione pelo menos um equipamento',
      }).toUpperCase()
    );
    for (const equipItem of selectedEquips) {
      const data = getSubmitEquipmentData(
        equipItem,
      );
      if (userDb) {
        try {
          updateEquipment(equipItem.id, data, userDb[0].accessToken)
            .then(resp => {
            })
            .catch(error => {
              console.error(error);
            })
        } catch (error) {
          toast.error(
            intl.formatMessage({
              id: 'equipment.Não foi possível editar os equipamentos. Tente novamente mais tarde',
            }).toUpperCase()
          );
        }
      }
    }
    if (success) {
      toast.success(
        intl.formatMessage({
          id: 'equipment.Acesso remoto concedido com sucesso!',
        }).toUpperCase())
      props.onCancel()
      props.onUpdateList && props.onUpdateList()
    }
  }

  const loadEquipment = useCallback(async () => {
    if (!userDb?.length) return;
    try {
      setIsLoading(true);
      isFetchingEquipmentsRef.current = true;
      equipmentRawRef.current = [];
      lastSeenEquipmentIdRef.current = undefined;

      const response = await loadEquipmentByCompanyId({
        user: userDb[0],
        accessToken: userDb[0].accessToken,
        limitResult: EQUIPMENT_PAGE_SIZE
      })
      const equipmentListResponse = Array.isArray(response.data) ? response.data : [];
      lastSeenEquipmentIdRef.current = equipmentListResponse[equipmentListResponse.length - 1]?.id;
      updateHasMore(equipmentListResponse.length === EQUIPMENT_PAGE_SIZE);
      syncEquipmentState(equipmentListResponse);
    } catch (error) {
      console.error(error)
    } finally {
      isFetchingEquipmentsRef.current = false;
      setIsLoading(false);
    }
  }, [userDb, syncEquipmentState, updateHasMore]);

  const handleLoadEquipments = useCallback(async () => {
    if (!userDb?.length) return;
    if (isFetchingEquipmentsRef.current || !hasMoreRef.current) return;

    try {
      isFetchingEquipmentsRef.current = true;

      const response = await loadEquipmentByCompanyId({
        user: userDb[0],
        accessToken: userDb[0].accessToken,
        lastSeenId: lastSeenEquipmentIdRef.current,
        limitResult: EQUIPMENT_PAGE_SIZE
      });

      const newEquipmentList = Array.isArray(response.data) ? response.data : [];

      if (newEquipmentList.length === 0) {
        updateHasMore(false);
        return;
      }

      lastSeenEquipmentIdRef.current = newEquipmentList[newEquipmentList.length - 1]?.id;

      const updatedList = mergeById([...equipmentRawRef.current, ...newEquipmentList]);
      syncEquipmentState(updatedList);

      if (newEquipmentList.length < EQUIPMENT_PAGE_SIZE) updateHasMore(false);
    } catch (error) {
      console.error('Erro ao carregar equipamentos com scroll:', error)
    } finally {
      isFetchingEquipmentsRef.current = false;
    }
  }, [userDb, syncEquipmentState, updateHasMore]);

  const handleScrollContent = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    createScrollHandler(isLoading, hasMore, handleLoadEquipments, scrollTimeoutRef)(e)
  }, [isLoading, hasMore, handleLoadEquipments])

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      const contentBody = contentBodyRef.current;

      if (
        modal !== 'modal2' ||
        !contentBody ||
        isFetchingEquipmentsRef.current ||
        !hasMoreRef.current
      ) {
        return;
      }

      const shouldPreloadNextPage =
        equipmentList.length <= EQUIPMENT_PAGE_SIZE ||
        contentBody.scrollHeight <= contentBody.clientHeight + 24;

      if (shouldPreloadNextPage) {
        handleLoadEquipments();
      }
    }, 50);

    return () => window.clearTimeout(timeoutId);
  }, [paginationTick, equipmentList.length, modal, handleLoadEquipments]);

  const loadCompany = useCallback(async () => {
    if (props.companyIdRemoteAccess) {
      try {
        setIsLoading(true);
        const ids = props.companyIdRemoteAccess.split(';').filter(Boolean);
        const companies = [];

        for (const id of ids) {
          try {
            const response = await loadCompanyById(id.trim());
            let company = response?.data;
            if (company) {
              companies.push(company);
            }
          } catch (error) {
            console.error(error);
          }
        }

        setCompanyList(companies);
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    }
  }, [props.companyIdRemoteAccess]);

  const markAll = async (event: ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    setCheckAll(isChecked);

    if (!isChecked) {
      setCheckAll(false);
      setSelectedEquips([]);
      return;
    }

    try {
      setIsLoading(true);
      isFetchingEquipmentsRef.current = true;
      const allEquipments = await loadAllEquipments();
      syncEquipmentState(allEquipments);
      lastSeenEquipmentIdRef.current = allEquipments[allEquipments.length - 1]?.id;
      setSelectedEquips(allEquipments.filter((equipment) => equipment.id !== props.equipmentId));
      updateHasMore(false);
    } catch (error) {
      console.error('Erro ao selecionar todos os equipamentos:', error);
      setCheckAll(false);
      setSelectedEquips([]);
      toast.error(
        intl.formatMessage({
          id: 'equipment.Não foi possível editar os equipamentos. Tente novamente mais tarde',
        }).toUpperCase()
      );
    } finally {
      isFetchingEquipmentsRef.current = false;
      setIsLoading(false);
    }
  };

  const setCheckEquip = (event: ChangeEvent<HTMLInputElement>, equip: any) => {
    const isChecked = event.target.checked;

    setCheckAll(false);
    setSelectedEquips((prevSelected) => isChecked
      ? mergeById([...prevSelected, equip])
      : prevSelected.filter(e => e.id !== equip.id)
    );
  };

  useEffect(() => {
    loadEquipment();
    loadCompany();
  }, [loadEquipment, loadCompany]);

  const modal1 = (
    <>
      <Styled.Text2>{intl.formatMessage({ id: 'equipment.Deseja selecionar outros equipamentos vinculados à sua conta para conceder acesso remoto a eles?' })}</Styled.Text2>
    </>
  );

  const modal2 = (
    <>
      <Styled.Text>
        {intl.formatMessage({ id: 'equipment.Selecione abaixo os equipamentos para os quais deseja conceder acesso remoto à' })}
        {companyList.map(({ company }, index) => {
          const isLast = index === companyList.length - 1;
          return ` ${company.id} - ${company.corporateName}${isLast ? '.' : '; '}`;
        })}
      </Styled.Text>

      <InputCheckBox
        id={'all'}
        label={'menus.Selecionar todos'}
        isChecked={checkAll}
        name={'Option'}
        onChange={(e) => markAll(e)}
      />

      <Styled.Columns ref={contentBodyRef} onScroll={handleScrollContent}>
        {equipmentList && equipmentList.map((item: any) => (
          <InputCheckBox
            key={item.id}
            id={String(item.id)}
            label={`${item.typeEquipment} - ${item.name}`}
            isChecked={Boolean(selectedEquips.find(e => e.id === item.id))}
            name="CheckEquip"
            onChange={(e) => setCheckEquip(e, item)}
          />
        ))}
      </Styled.Columns>
    </>
  );

  const footer1 = (
    <>
      <PrimaryButton type='button' size='buttonModal' model='confirmation-gradient-modal' width='68px' onClick={() => setModal("modal2")}>
        {intl.formatMessage({ id: 'menus.Sim' })}
      </PrimaryButton>
      <PrimaryButton type='button' size='buttonModal' model='cancellation' width='68px' onClick={props.onCancel}>
        {intl.formatMessage({ id: 'menus.Não' })}
      </PrimaryButton>
    </>
  );

  const footer2 = (
    <>
      <PrimaryButton type='button' size='buttonModal' model='confirmation-gradient-modal' width='108px' onClick={submitHandler}>
        {intl.formatMessage({ id: 'equipment.confirmar' })}
      </PrimaryButton>
      <PrimaryButton type='button' size='buttonModal' model='cancellation' width='108px' onClick={props.onCancel}>
        {intl.formatMessage({ id: 'menus.CANCELAR' })}
      </PrimaryButton>
    </>
  );

  return (
    <Modal
      title={intl.formatMessage({ id: 'equipment.Acesso remoto' })}
      onCancel={props.onCancel}
      children={modal === "modal1" ? modal1 : modal2}
      footer={modal === 'modal1' ? footer1 : footer2}
      width={modal === "modal1" ? '450px' : '650px'}
    />
  );
};

export default ModalRemoteAccess;
